import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  IconButton,
  Chip,
  Collapse,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Event as EventIcon,
  Payment as PaymentIcon,
} from '@mui/icons-material';
import { THEME_COLORS } from '../../constants';

/**
 * Componente de tarjeta de suscripción para vista móvil
 */
const SubscriptionCard = ({
  subscription,
  isExpanded,
  onExpand,
  onEdit,
  onDelete,
  showActions = true,
}) => {
  const handleExpandClick = () => {
    onExpand(subscription.subscription_id);
  };

  const handleEditClick = (e) => {
    e.stopPropagation();
    onEdit(subscription);
  };

  const handleDeleteClick = (e) => {
    e.stopPropagation();
    onDelete(subscription.subscription_id);
  };

  // Determinar el color del estado - Solo tema minimalista blanco/negro/gris
  const getStatusColor = (status, isExpired, isExpiringSoon) => {
    if (isExpired || status === 'cancelada') {
      return THEME_COLORS.TEXT_SECONDARY; // Gris oscuro para vencidas/canceladas
    }
    if (isExpiringSoon || status === 'suspendida') {
      return THEME_COLORS.TEXT_MUTED; // Gris medio para suspendidas/venciendo
    }
    if (status === 'activa') {
      return THEME_COLORS.TEXT_PRIMARY; // Negro para activas
    }
    return THEME_COLORS.TEXT_SECONDARY; // Gris por defecto
  };

  // Obtener texto del estado con indicadores adicionales
  const getStatusText = (subscription) => {
    if (subscription.isExpired) return 'Vencida';
    if (subscription.isExpiringSoon) return `Vence en ${subscription.daysRemaining} días`;
    return subscription.statusLabel;
  };

  return (
    <Card
      sx={{
        mb: 2,
        backgroundColor: THEME_COLORS.BACKGROUND_PAPER,
        border: `1px solid ${THEME_COLORS.BORDER_LIGHT}`,
        cursor: 'pointer',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          transform: 'translateY(-1px)',
          boxShadow: `0 2px 8px ${THEME_COLORS.SHADOW_LIGHT}`,
        },
      }}
      onClick={handleExpandClick}
    >
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        {/* Header con información principal */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                color: THEME_COLORS.TEXT_PRIMARY,
                fontSize: '1rem',
                lineHeight: 1.3,
                mb: 0.5,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {subscription.contact_name}
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Chip
                label={getStatusText(subscription)}
                size="small"
                sx={{
                  backgroundColor: getStatusColor(
                    subscription.estado,
                    subscription.isExpired,
                    subscription.isExpiringSoon
                  ),
                  color: 'white',
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  height: 24,
                }}
              />
              
              <Chip
                label={subscription.planLabel}
                size="small"
                variant="outlined"
                sx={{
                  borderColor: THEME_COLORS.BORDER_LIGHT,
                  color: THEME_COLORS.TEXT_SECONDARY,
                  fontSize: '0.75rem',
                  height: 24,
                }}
              />
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {showActions && (
              <>
                <IconButton
                  size="small"
                  onClick={handleEditClick}
                  sx={{
                    color: THEME_COLORS.TEXT_SECONDARY,
                    '&:hover': {
                      backgroundColor: THEME_COLORS.BACKGROUND_DEFAULT,
                      color: THEME_COLORS.TEXT_PRIMARY,
                    },
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>

                <IconButton
                  size="small"
                  onClick={handleDeleteClick}
                  title="Eliminar suscripción"
                  sx={{
                    color: THEME_COLORS.TEXT_SECONDARY,
                    '&:hover': {
                      backgroundColor: THEME_COLORS.BACKGROUND_DEFAULT,
                      color: THEME_COLORS.TEXT_PRIMARY,
                    },
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </>
            )}

            <IconButton
              size="small"
              sx={{
                color: THEME_COLORS.TEXT_SECONDARY,
                transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s ease-in-out',
              }}
            >
              <ExpandMoreIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        {/* Información básica siempre visible */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography
            variant="body2"
            sx={{
              color: THEME_COLORS.TEXT_SECONDARY,
              fontSize: '0.875rem',
            }}
          >
            Vence: {subscription.formattedEndDate}
          </Typography>
          
          {subscription.formattedAmount !== 'Sin monto' && (
            <Typography
              variant="body2"
              sx={{
                color: THEME_COLORS.TEXT_PRIMARY,
                fontSize: '0.875rem',
                fontWeight: 600,
              }}
            >
              {subscription.formattedAmount}
            </Typography>
          )}
        </Box>

        {/* Contenido expandible */}
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${THEME_COLORS.BORDER_LIGHT}` }}>
            {/* Información de contacto */}
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="subtitle2"
                sx={{
                  color: THEME_COLORS.TEXT_PRIMARY,
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  mb: 1,
                }}
              >
                Información de Contacto
              </Typography>

              {subscription.contact_phone && (
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                  <PhoneIcon sx={{ fontSize: 16, color: THEME_COLORS.TEXT_SECONDARY, mr: 1 }} />
                  <Typography variant="body2" sx={{ color: THEME_COLORS.TEXT_SECONDARY }}>
                    {subscription.contact_phone}
                  </Typography>
                </Box>
              )}

              {subscription.contact_email && (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <EmailIcon sx={{ fontSize: 16, color: THEME_COLORS.TEXT_SECONDARY, mr: 1 }} />
                  <Typography variant="body2" sx={{ color: THEME_COLORS.TEXT_SECONDARY }}>
                    {subscription.contact_email}
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Detalles de suscripción */}
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="subtitle2"
                sx={{
                  color: THEME_COLORS.TEXT_PRIMARY,
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  mb: 1,
                }}
              >
                Detalles de Suscripción
              </Typography>

              <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                <EventIcon sx={{ fontSize: 16, color: THEME_COLORS.TEXT_SECONDARY, mr: 1 }} />
                <Typography variant="body2" sx={{ color: THEME_COLORS.TEXT_SECONDARY }}>
                  Inicio: {subscription.formattedStartDate}
                </Typography>
              </Box>

              {subscription.metodo_pago && (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <PaymentIcon sx={{ fontSize: 16, color: THEME_COLORS.TEXT_SECONDARY, mr: 1 }} />
                  <Typography variant="body2" sx={{ color: THEME_COLORS.TEXT_SECONDARY }}>
                    {subscription.metodo_pago}
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Notas si existen */}
            {subscription.notas && (
              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: THEME_COLORS.TEXT_PRIMARY,
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Notas
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: THEME_COLORS.TEXT_SECONDARY,
                    fontStyle: 'italic',
                  }}
                >
                  {subscription.notas}
                </Typography>
              </Box>
            )}
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
};

export default SubscriptionCard; 